import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import capitalize from 'lodash/capitalize';
import { Collapse } from '@material-ui/core';
import { Badge, RichTextViewer, Switch, Typography } from 'seagull';
import { WideArrowDownward, WideArrowForward, WideArrowUpward } from 'seagull/icons';
import { getServiceBillingText, getServicePaymentText } from "../../../utils/service-description";
import { getUnitName } from "../../../utils/unit-name.utils";
import { useGlobalContext } from "../../../contexts";
import { ResponsiveModal } from "../../components/ResponsiveModal";
import { useResponsive } from "../../contexts";
import { getBillingBadgeText } from "./Service.utils";
import styles from "./Service.module.scss";
import { utils } from "../../../utils";
import { OverrideBranding } from "../CobBrandingContext";
import { Intent } from "../../../infra/access-control";
export function Service(_ref) {
  let {
    idx,
    relationship,
    service,
    optionallySelected,
    onChangeOptionallySelected
  } = _ref;
  const globalContext = useGlobalContext();
  const {
    isDesktop,
    isMobile
  } = useResponsive();
  const [showContent, setShowContent] = useState(false);
  const isOptional = service.participation.type === 'optional';
  const getTypographySkin = defaultSkin => isOptional && !optionallySelected ? 'secondary' : defaultSkin || 'standard';
  const discountBadge = service.cost.discount ? /*#__PURE__*/React.createElement(Badge, {
    rounded: true,
    outlined: true,
    skin: "success"
  }, utils.formatDiscount(service.cost.discount), " off") : /*#__PURE__*/React.createElement(React.Fragment, null);
  const billingBadge = /*#__PURE__*/React.createElement(Badge, {
    rounded: true,
    outlined: true
  }, getBillingBadgeText(service).text);
  function priceComplex() {
    var _service$cost$discoun, _service$cost$discoun2;
    switch (service.cost.pricingType) {
      case 'fixed':
      case 'variable':
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
          variant: "h6",
          skin: getTypographySkin()
        }, utils.formatDollarAmount(utils.applyDiscount(service.cost.price, service.cost.discount))), ((_service$cost$discoun = service.cost.discount) === null || _service$cost$discoun === void 0 ? void 0 : _service$cost$discoun.amount) && /*#__PURE__*/React.createElement(Typography, {
          skin: getTypographySkin('secondary'),
          textDecoration: "line-through",
          className: styles.priceAbove
        }, utils.formatDollarAmount(service.cost.price)), /*#__PURE__*/React.createElement(Typography, {
          variant: "paragraph2",
          skin: getTypographySkin('secondary')
        }, service.cost.pricingType === 'variable' ? "/ ".concat(capitalize(getUnitName(service.cost.unitName))) : ''));
      case 'range':
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
          variant: "h6",
          skin: getTypographySkin()
        }, service.cost.minPrice && !service.cost.minPrice.eq(0) ? "".concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.minPrice, service.cost.discount)), " - ") : 'Up to ', utils.formatDollarAmount(utils.applyDiscount(service.cost.maxPrice, service.cost.discount))), ((_service$cost$discoun2 = service.cost.discount) === null || _service$cost$discoun2 === void 0 ? void 0 : _service$cost$discoun2.amount) && /*#__PURE__*/React.createElement(Typography, {
          skin: getTypographySkin('secondary'),
          textDecoration: "line-through",
          className: styles.priceAbove
        }, !!service.cost.minPrice && "".concat(utils.formatDollarAmount(service.cost.minPrice), " - "), utils.formatDollarAmount(service.cost.maxPrice)));
      default:
        return null;
    }
  }
  function openServiceModal() {
    return globalContext.showModal({
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/React.createElement(ResponsiveModal, {
          backTitle: "Back to agreement",
          title: service.name,
          isDesktop: isDesktop,
          onClose: dismissModal
        }, /*#__PURE__*/React.createElement("div", {
          className: styles.mobileModalPrice,
          "data-testid": "service-modal-pricing"
        }, /*#__PURE__*/React.createElement("div", {
          className: styles.mobileModalPriceInnerRow
        }, priceComplex()), /*#__PURE__*/React.createElement("div", null, discountBadge)), /*#__PURE__*/React.createElement(ServiceDetails, {
          idx: idx,
          relationship: relationship,
          service: service
        }));
      }
    });
  }
  const optionalSwitch = isOptional && /*#__PURE__*/React.createElement(Switch, {
    intent: Intent.SelectOptionalService,
    checked: optionallySelected,
    "data-testid": "agreement-terms-service-".concat(idx, "-optional-toggle"),
    onClick: e => e.stopPropagation() // So we don't toggle the collapse.
    ,
    onChange: () => onChangeOptionallySelected && onChangeOptionallySelected(!optionallySelected)
  });
  return /*#__PURE__*/React.createElement("div", {
    className: styles.root
  }, !isMobile ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.accordionTitle,
    "data-testid": "agreement-terms-service-".concat(idx),
    onClick: () => setShowContent(!showContent)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.nameWrapper
  }, optionalSwitch, /*#__PURE__*/React.createElement(Typography, {
    className: styles.name,
    ellipsis: true,
    weight: "normal",
    skin: getTypographySkin()
  }, service.name)), /*#__PURE__*/React.createElement(OverrideBranding, {
    className: styles.badges
  }, discountBadge, billingBadge), /*#__PURE__*/React.createElement("div", {
    className: styles.price
  }, priceComplex()), /*#__PURE__*/React.createElement("div", {
    className: styles.toggleIcon
  }, showContent ? /*#__PURE__*/React.createElement(WideArrowUpward, null) : /*#__PURE__*/React.createElement(WideArrowDownward, null))), /*#__PURE__*/React.createElement(Collapse, {
    in: showContent
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.accordionContent
  }, /*#__PURE__*/React.createElement(ServiceDetails, {
    idx: idx,
    relationship: relationship,
    service: service
  })))) : /*#__PURE__*/React.createElement("div", {
    onClick: openServiceModal,
    className: styles.mobileButton,
    "data-testid": "agreement-terms-service-mobile-".concat(idx)
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.mobileHeader, styles.nameWrapper)
  }, optionalSwitch, /*#__PURE__*/React.createElement(Typography, {
    className: styles.mobileTitle,
    weight: "normal",
    skin: getTypographySkin()
  }, service.name)), /*#__PURE__*/React.createElement("div", {
    className: styles.mobileDetails,
    "data-testid": "agreement-terms-service-pricing"
  }, priceComplex()), /*#__PURE__*/React.createElement(OverrideBranding, {
    className: styles.mobileBadges
  }, discountBadge, billingBadge)), /*#__PURE__*/React.createElement("div", {
    className: styles.mobileChevron
  }, /*#__PURE__*/React.createElement(WideArrowForward, null))));
}
function ServiceDetails(_ref3) {
  let {
    relationship,
    service,
    idx
  } = _ref3;
  const {
    isMobile
  } = useResponsive();
  const billing = useMemo(() => {
    return getServiceBillingText(service, {});
  }, [service]);
  const payment = useMemo(() => {
    const {
      cost
    } = service;
    const isVariablePrice = cost.pricingType === 'variable';
    return getServicePaymentText({
      unitCap: isVariablePrice ? cost.unitCap : undefined,
      unitName: isVariablePrice ? cost.unitName : undefined,
      maxPrice: cost.pricingType === 'range' ? cost.maxPrice : undefined
    }, {
      netTerms: relationship.netTerms,
      autoPayment: !relationship.allowManualPayments
    });
  }, [relationship, service]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.serviceDetails
  }, service.description && /*#__PURE__*/React.createElement("section", {
    className: styles.section
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.sectionTitle, {
      [styles.mobile]: isMobile
    })
  }, /*#__PURE__*/React.createElement(Typography, null, "Service details")), /*#__PURE__*/React.createElement(RichTextViewer, {
    fitContent: true,
    value: service.description,
    skin: "secondary"
  })), !!billing.length && /*#__PURE__*/React.createElement("section", {
    className: styles.section
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.sectionTitle, {
      [styles.mobile]: isMobile
    })
  }, /*#__PURE__*/React.createElement(Typography, null, "Billing")), /*#__PURE__*/React.createElement("ul", {
    className: styles.billingBullets,
    "data-testid": "agreement-terms-service-".concat(idx, "-billing")
  }, billing.map((_ref4, i) => {
    let {
      text,
      intent
    } = _ref4;
    return /*#__PURE__*/React.createElement("li", {
      key: intent,
      className: styles.listItem,
      "data-intent": intent || undefined,
      "data-testid": "agreement-terms-service-".concat(idx, "-billing-bullet-").concat(i)
    }, /*#__PURE__*/React.createElement(Typography, {
      skin: "secondary"
    }, text));
  }))), !!payment.length && /*#__PURE__*/React.createElement("section", {
    className: styles.section
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.sectionTitle, {
      [styles.mobile]: isMobile
    })
  }, /*#__PURE__*/React.createElement(Typography, null, "Payment")), /*#__PURE__*/React.createElement("ul", {
    "data-testid": "agreement-terms-service-".concat(idx, "-payment")
  }, payment.map((_ref5, i) => {
    let {
      text,
      intent
    } = _ref5;
    return /*#__PURE__*/React.createElement("li", {
      key: intent,
      className: styles.listItem,
      "data-intent": intent || undefined,
      "data-testid": "agreement-terms-service-".concat(idx, "-payment-bullet-").concat(i)
    }, /*#__PURE__*/React.createElement(Typography, {
      skin: "secondary"
    }, text));
  }))));
}